class CartGiftManager {
  static instances = new Set();
  static evaluating = false;

  static register(instance) {
    this.instances.add(instance);
  }

  static unregister(instance) {
    this.instances.delete(instance);
  }

  static fetchCart() {
    return fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((response) => response.json());
  }

  static async refreshCartDrawer(sectionId = 'cart-drawer') {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=${sectionId}`);

    if (!response.ok) return;

    const sections = await response.json();
    if (sections[sectionId] && typeof window.updateCartContents === 'function') {
      window.updateCartContents({ sections: { [sectionId]: sections[sectionId] } });
    }
  }

  static async finalizeMutation(result) {
    const sectionId = 'cart-drawer';
    const hasSections = Boolean(result?.sections?.[sectionId]);

    if (hasSections && typeof window.updateCartContents === 'function') {
      window.updateCartContents(result);
    }

    if (result?.items) {
      window.cart = result;
    } else {
      window.cart = await this.fetchCart();
      if (!hasSections) {
        await this.refreshCartDrawer(sectionId);
      }
    }

    return window.cart;
  }

  static evaluateAll(cart) {
    if (this.evaluating || !this.instances.size) {
      return Promise.resolve();
    }

    return this.runEvaluateAll(cart);
  }

  static async runEvaluateAll(cart) {
    this.evaluating = true;
    try {
      let currentCart = cart;
      for (const instance of [...this.instances]) {
        await instance.evaluate(currentCart);
        if (window.cart?.items) {
          currentCart = window.cart;
        }
      }
    } finally {
      this.evaluating = false;
    }
  }

  static scheduleEvaluate() {
    if (!window.cart?.items || !this.instances.size || this.evaluating) return;

    clearTimeout(this._scheduleTimer);
    this._scheduleTimer = setTimeout(() => {
      this.evaluateAll(window.cart);
    }, 0);
  }
}

class CartFreeGift extends HTMLElement {
  static CART_GIFT_PROPERTY = '_Cart Gift';

  connectedCallback() {
    this._busy = false;
    this.config = this.readConfig();
    this.handleDocumentClick = (event) => this.onDocumentClick(event);
    document.addEventListener('click', this.handleDocumentClick);
    CartGiftManager.register(this);
    CartGiftManager.scheduleEvaluate();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    CartGiftManager.unregister(this);
  }

  readConfig() {
    return {
      variantId: Number(this.dataset.variantId),
      productId: Number(this.dataset.productId),
      threshold: Number(this.dataset.threshold || 0),
      optOutKey: this.dataset.optOutKey || `cart-free-gift-opt-out-${this.dataset.variantId}`,
      giftName: this.dataset.giftName || '',
      inventory: Number(this.dataset.inventory || 0),
      hasVariants: this.hasAttribute('has-variants'),
      lineSelector: this.dataset.lineSelector || '',
      sectionId: this.dataset.sectionId || 'cart-drawer',
      staleProductType: this.dataset.staleProductType || '',
      legacyFreeGift: this.hasAttribute('legacy-free-gift'),
      excludeTypes: (this.dataset.excludeTypes || 'Gift Card,Greened Shipping')
        .split(',')
        .map((type) => type.trim())
        .filter(Boolean),
    };
  }

  onDocumentClick(event) {
    const trigger = event.target.closest(`[data-cart-free-gift-id="${this.config.productId}"]`);
    if (!trigger) return;

    if (trigger.matches('[data-cart-free-gift-remove]')) {
      event.preventDefault();
      this.remove(true);
    }

    if (trigger.matches('[data-cart-free-gift-add]')) {
      event.preventDefault();
      this.add();
    }
  }

  getLineContainer() {
    if (!this.config.lineSelector) return null;
    return document.querySelector(this.config.lineSelector);
  }

  hasCartGiftProperty(item) {
    return Boolean(item.properties?.[CartFreeGift.CART_GIFT_PROPERTY]);
  }

  isCartGiftLineItem(item) {
    if (item.product_id !== this.config.productId) return false;
    if (this.hasCartGiftProperty(item)) return true;
    if (this.config.legacyFreeGift && item.product_type === 'Free Gift') return true;
    return false;
  }

  getCartSubtotal(cart) {
    return cart.items.reduce((sum, item) => {
      const productType = item.product_type || '';
      if (this.config.excludeTypes.some((type) => productType.includes(type))) {
        return sum;
      }
      if (this.hasCartGiftProperty(item)) {
        return sum;
      }
      if (item.product_type === 'Free Gift') {
        return sum;
      }
      return sum + item.final_line_price;
    }, 0);
  }

  findGiftInCart(cart) {
    return cart.items.filter((item) => this.isCartGiftLineItem(item));
  }

  findStaleGiftsInCart(cart) {
    if (!this.config.staleProductType) return [];
    return cart.items.filter(
      (item) =>
        item.product_type === this.config.staleProductType &&
        item.product_id !== this.config.productId
    );
  }

  isOptedOut() {
    return sessionStorage.getItem(this.config.optOutKey) === 'true';
  }

  setOptedOut(value) {
    if (value) {
      sessionStorage.setItem(this.config.optOutKey, 'true');
    } else {
      sessionStorage.removeItem(this.config.optOutKey);
    }
  }

  disableCheckout() {
    document.querySelector('#checkout-button')?.classList.add('disabled');
  }

  finishUserMutation() {
    if (typeof window.updateCartStuff === 'function') {
      window.updateCartStuff(window.cart, { skipGiftSync: true });
    }
  }

  showOptOutState(lineContainer) {
    if (!lineContainer) return;

    lineContainer.hidden = false;
    lineContainer.querySelectorAll('.cart-item').forEach((item) => {
      item.hidden = true;
    });

    const optOutState = lineContainer.querySelector('[data-gift-opt-out-state]');
    if (optOutState) {
      optOutState.hidden = false;
    }

    document.querySelector('.slide-out-cart-body')?.classList.add('gift-waiting');
  }

  async removeStaleGifts(cart) {
    const staleGifts = this.findStaleGiftsInCart(cart);
    if (!staleGifts.length || this._busy) return false;

    this._busy = true;
    this.disableCheckout();

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `updates[${staleGifts[0].key}]=0&sections=${this.config.sectionId}`,
      });
      await CartGiftManager.finalizeMutation(await response.json());
      return true;
    } finally {
      this._busy = false;
    }
  }

  async evaluate(cart) {
    if (this._busy || !this.config.variantId || !this.config.productId) return;

    if (await this.removeStaleGifts(cart)) return;

    const cartSubtotal = this.getCartSubtotal(cart);
    const giftInCart = this.findGiftInCart(cart);
    const lineContainer = this.getLineContainer();
    const { threshold, inventory } = this.config;
    const thresholdMet = cartSubtotal >= threshold;
    const inStock = inventory > 0;
    const cartBody = document.querySelector('.slide-out-cart-body');

    if (!thresholdMet) {
      if (giftInCart.length) {
        await this.remove(false, { fromEvaluate: true });
      } else {
        cartBody?.classList.remove('gift-waiting');
      }
      return;
    }

    if (!inStock) return;

    if (giftInCart.length) {
      if (giftInCart[0].quantity > 1) {
        await this.setQuantity(giftInCart[0].key, 1, { fromEvaluate: true });
        return;
      }
      cartBody?.classList.remove('gift-waiting');
      return;
    }

    if (this.isOptedOut()) {
      this.showOptOutState(lineContainer);
      return;
    }

    await this.add({ fromEvaluate: true });
    cartBody?.classList.remove('gift-waiting');
  }

  async setQuantity(lineItemKey, quantity, { fromEvaluate = false } = {}) {
    if (this._busy) return;

    this._busy = true;
    this.disableCheckout();

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `updates[${lineItemKey}]=${quantity}&sections=${this.config.sectionId}`,
      });
      await CartGiftManager.finalizeMutation(await response.json());
      if (!fromEvaluate) this.finishUserMutation();
    } finally {
      this._busy = false;
    }
  }

  async remove(optOut = false, { fromEvaluate = false } = {}) {
    if (this._busy) return;

    const cart = window.cart || (await CartGiftManager.fetchCart());
    const giftItem = this.findGiftInCart(cart)[0];
    if (!giftItem) return;

    this._busy = true;
    const lineContainer = this.getLineContainer();

    if (lineContainer && optOut) {
      const title = lineContainer.querySelector('.cart-item .cart-item-title');
      if (title) {
        title.textContent = `Removing ${this.config.giftName} from your bag...`;
      }
    }

    this.disableCheckout();

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `updates[${giftItem.key}]=0&sections=${this.config.sectionId}`,
      });
      await CartGiftManager.finalizeMutation(await response.json());

      if (optOut) {
        this.setOptedOut(true);
        this.showOptOutState(lineContainer);
      }

      if (!fromEvaluate) this.finishUserMutation();
    } finally {
      this._busy = false;
    }
  }

  async add({ fromEvaluate = false } = {}) {
    if (this._busy) return;

    const cart = window.cart || (await CartGiftManager.fetchCart());
    if (this.findGiftInCart(cart).length) return;

    this._busy = true;
    document.querySelector('.slide-out-cart-body')?.classList.remove('gift-waiting');

    const payload = {
      items: [
        {
          id: this.config.variantId,
          quantity: 1,
          properties: {
            [CartFreeGift.CART_GIFT_PROPERTY]: 'true',
            ...(this.config.hasVariants ? { _Variants: 'true' } : {}),
          },
        },
      ],
      sections: this.config.sectionId,
    };

    this.disableCheckout();

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      await CartGiftManager.finalizeMutation(await response.json());
      this.setOptedOut(false);
      if (!fromEvaluate) this.finishUserMutation();
    } finally {
      this._busy = false;
    }
  }
}

if (!customElements.get('cart-free-gift')) {
  customElements.define('cart-free-gift', CartFreeGift);
}

window.CartGiftManager = CartGiftManager;
